import * as React from 'react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Field, useFormikContext } from 'formik'
import { produce } from 'immer'
import { BaseTextField, FormAddress } from '..'
import { AddressSearch } from './AddressSearch/AddressSearch'
import { formAddressToString, isValidAddress } from './typings'
import { useGeocode } from './hooks'
import Grid, { GridProps } from '@mui/material/Grid'
import { TextFieldProps } from '@mui/material/TextField'
import { useTranslation } from 'react-i18next'
import { HereResult } from '@user/store/services/hereGeoCoder'

interface Props {
  disabled?: boolean
  disableSearch?: boolean
  fieldNamePrefix?: string
  spacing?: GridProps['spacing']
  textFieldProps?: TextFieldProps
  required?: boolean
  avoidValidateOnBlur?: boolean
  enforcePPLengthRequirements?: boolean
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean,
  ) => void
  onAddressBlur?: (address: string) => void
  onSelect?: (place: HereResult) => void
}

export const AddressFields: FC<Props> = (props) => {
  const {
    spacing = 2,
    textFieldProps,
    disabled = false,
    required = false,
    avoidValidateOnBlur = false,
    setFieldTouched,
    fieldNamePrefix = '',
    onAddressBlur,
    onSelect,
  } = props

  const { values, handleChange, setValues, setFieldValue } =
    useFormikContext<FormAddress>()
  // If the form already has a usable address on first render, kick off geocoding
  const [geocode, setGeocode] = useState(() => {
    const initialAddress = formAddressToString(values)
    return initialAddress.trim().length > 0
  })
  const { result, execute } = useGeocode(2000)

  const validAddress = useMemo(() => isValidAddress(values), [values])
  const address = useMemo(() => formAddressToString(values), [values])
  const { t } = useTranslation()

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(event)
      if (!avoidValidateOnBlur) {
        setFieldTouched(`${fieldNamePrefix}postalCode`, true, true)
        setFieldTouched(`${fieldNamePrefix}state`, true, true)
      }
      setGeocode(true)
    },
    [handleChange, avoidValidateOnBlur, setFieldTouched, fieldNamePrefix],
  )

  const onBlur = useCallback(() => {
    if (onAddressBlur) {
      onAddressBlur(address)
    }
  }, [onAddressBlur, address])

  useEffect(() => {
    if (geocode && validAddress) {
      execute(address)
    }
  }, [address, execute, geocode, validAddress])

  useEffect(() => {
    if (!!result) {
      const {
        address: { countryCode: country, county },
        position: { lat, lng },
      } = result

      setValues((current) =>
        produce(current, (draft) => {
          draft[`${fieldNamePrefix}country`] = country
          draft[`${fieldNamePrefix}county`] = county
          draft[`${fieldNamePrefix}lat`] = lat
          draft[`${fieldNamePrefix}lng`] = lng
        }),
      )
    } else {
      // Clear out lat/lng if geocode failed
      setValues((current) =>
        produce(current, (draft) => {
          draft[`${fieldNamePrefix}lat`] = 0
          draft[`${fieldNamePrefix}lng`] = 0
        }),
      )
    }
  }, [fieldNamePrefix, result, setValues])

  useEffect(() => {
    if (!validAddress) {
      // Clear out lat/lng if invalid address
      setValues((current) =>
        produce(current, (draft) => {
          draft[`${fieldNamePrefix}lat`] = 0
          draft[`${fieldNamePrefix}lng`] = 0
        }),
      )
    }
  }, [validAddress, setValues, fieldNamePrefix, values])

  return (
    <Grid container item spacing={spacing}>
      <Grid item xs={12}>
        {props.disableSearch ? (
          <Field
            name={`${fieldNamePrefix}address1`}
            label="Address"
            component={BaseTextField}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            {...textFieldProps}
            required={required}
            avoidValidateOnBlur={avoidValidateOnBlur}
            data-test-id={`${fieldNamePrefix}-address1`}
          />
        ) : (
          <AddressSearch
            path={fieldNamePrefix}
            setGeocode={setGeocode}
            textFieldProps={textFieldProps}
            disabled={disabled}
            required={required}
            setFieldTouched={setFieldTouched}
            onAddressBlur={onBlur}
            avoidValidateOnBlur={avoidValidateOnBlur}
            onSelect={onSelect}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <Field
          name={`${fieldNamePrefix}address2`}
          label="Apt / Suite"
          component={BaseTextField}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          {...textFieldProps}
          avoidValidateOnBlur={avoidValidateOnBlur}
          data-test-id={`${fieldNamePrefix}-address2`}
        />
      </Grid>
      <Grid item md={4} xs={12}>
        <Field
          name={`${fieldNamePrefix}city`}
          label="City"
          component={BaseTextField}
          onChange={onChange}
          disabled={disabled}
          required={required}
          {...textFieldProps}
          onBlur={onBlur}
          avoidValidateOnBlur={avoidValidateOnBlur}
          data-test-id={`${fieldNamePrefix}-city`}
        />
      </Grid>
      <Grid item md={4} xs={6}>
        <Field
          name={`${fieldNamePrefix}state`}
          label={t('state')}
          component={BaseTextField}
          onChange={onChange}
          disabled={disabled}
          required={required}
          {...textFieldProps}
          avoidValidateOnBlur={avoidValidateOnBlur}
          onBlur={() => {
            setFieldValue('assignmentRuleFieldFocused', false)
            onBlur()
          }}
          onFocus={() => setFieldValue('assignmentRuleFieldFocused', true)}
          data-test-id={`${fieldNamePrefix}-state`}
        />
      </Grid>
      <Grid item md={4} xs={6}>
        <Field
          name={`${fieldNamePrefix}postalCode`}
          label={t('postalCode')}
          component={BaseTextField}
          onChange={onChange}
          disabled={disabled}
          required={required}
          inputProps={{ maxLength: 10 }}
          {...textFieldProps}
          avoidValidateOnBlur={avoidValidateOnBlur}
          onBlur={() => {
            setFieldValue('assignmentRuleFieldFocused', false)
            onBlur()
          }}
          onFocus={() => setFieldValue('assignmentRuleFieldFocused', true)}
          data-test-id={`${fieldNamePrefix}-postal-code`}
        />
      </Grid>
    </Grid>
  )
}

AddressFields.displayName = 'AddressFields'
